import './App.css'
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom'
// @ts-ignore
import { createElement as jsx, useEffect, useMemo } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { useState } from 'react'

// @ts-ignore
import { getFromLS } from './utils/localStore/localStore.js'
// @ts-ignore
import useTranslation from './hooks/useTranslation'

import { useSnapshot } from 'valtio'

//Setting up Themes
import { ThemeProvider, createGlobalStyle } from 'styled-components'
import { ModalStackProvider } from './context/modalStackContext'
import manageClientId  from './utils/localStore/manageClientId'

// @ts-ignore
import { setupPersister } from './utils/queryPersister'
// @ts-ignore
import { authState } from './hooks/useAuth'
// @ts-ignore
import useTheme from './hooks/useTheme'
// @ts-ignore
import compileTheme from './styles/themes'

/* Importing Components*/
// @ts-ignore
import Loader from './components/generic/Loader'
// @ts-ignore
import SkeletonLoaderTest from './components/generic/SkeletonLoaderTest' // Used in the /test route
// @ts-ignore
import entryRoutes from './model/routes/entryRoutes'
// @ts-ignore
import { getModel, clearResolvedModel } from './model/registry'
// @ts-ignore
import { loadModel, watchModelVersion, applyPendingModel } from './model/loadModel'
// @ts-ignore
import useModelVersion from './hooks/useModelVersion'
// @ts-ignore
import portalRoutes from './model/routes/portalRoutes'
// @ts-ignore
import Welcome from './pages/Welcome'
// @ts-ignore
import LoggedOut from './pages/LoggedOut'
// @ts-ignore
import Missing from './pages/Missing'
// @ts-ignore
import Layout from './pages/Layout'
// @ts-ignore
import AppBoard from './pages/apps/home/Index'
// @ts-ignore
import Hello from './pages/apps/hello/HelloWorld'
// @ts-ignore
import CreateOrg from './pages/CreateOrg'

/* Importing Auth Verifier */
// @ts-ignore
import RequireAuth from './pages/entry/RequireAuth'

//Setup the react query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
})

const dEntryRoutes =
  entryRoutes.map(
    // @ts-ignore
    (route, i) => {
      return (
        <Route path={route.path} key={i} element={jsx(route.element, route.props)} />
      )
    })


// The app's routes, from the org's model (30.03.06 §3.2). Built inside App and
// memoized on the model version rather than at module scope, where it was
// evaluated once at import — before any org, so it could never see an overlay.
const buildViews = () =>
  getModel('viewRoutes').map(
    // @ts-ignore
    (route, i) => {
      return (
        // @ts-ignore
        <Route path={route.path} key={i} element={jsx(route.element, route.props)} />
      )
    }
  )

// Every response says which model version served it; a disagreement with the
// registry fetches the new one in the background (30.03.06 §4.1).
watchModelVersion()

/**
 * A model that changed while the user was on a screen — a deploy, a stale
 * token corrected — waits, and swaps in on the next navigation, never mid-form
 * (30.03.06 §6). Inside the Router so it can watch the location.
 */
function ModelSwap() {
  const { pathname } = useLocation()
  useEffect(() => { applyPendingModel() }, [pathname])
  return null
}

const dPortalRoutes = (
  <>
    <Route path={portalRoutes.layout.path} element={jsx(portalRoutes.layout.element, portalRoutes.layout.props)}>
      {portalRoutes.children.map((route: any, i: number) => (
        <Route key={i} path={route.path} element={jsx(route.element, route.props)} />
      ))}
    </Route>
    <Route path={portalRoutes.error.path} element={jsx(portalRoutes.error.element, portalRoutes.error.props)} />
  </>
)

// @ts-ignore
const GlobalStyle = createGlobalStyle`
  html, body {
    margin: 0;
    padding: 0;
    height: 100%;
  }
  #root {
    height: 100%;
  }
  body {
    background: ${({ theme }: any) => theme.area.bodyBg};
  }
`

function App() {
  //  let localMode = import.meta.env.VITE_LOCAL_MODE || false
  //  console.log(`Local Mode is: ${localMode} `)

  const { theme, ThemeContext } = useTheme()
  const { tState, loadTrans } = useTranslation()
  const snap = useSnapshot(tState)

  const orgId = useSnapshot(authState).data.orgId

  useEffect(() => {
    if (!orgId) return
    const [unsubscribe] = setupPersister(queryClient, orgId)
    return unsubscribe
  }, [orgId])

  // The org's model, loaded before anything is shown from it (30.03.06 §4).
  // `modelReadyFor` is the org it was loaded for, so the gate closes again on
  // a login or an org switch, not only at boot. No org — logged out, the portal —
  // is the base model, which needs no loading.
  //
  // The gate is an OVERLAY, never an unmount. The org changes in the middle of
  // flows — creating the first org sets it while the create-org wizard is open —
  // and returning a loader in place of the tree remounted that wizard at step
  // one, its state gone. So the routes stay mounted under the overlay and
  // re-render in place when the model arrives. Moving from one org to another
  // first drops to base, so one org's customizations never show over another's
  // data while the new model loads.
  const [modelReadyFor, setModelReadyFor] = useState<string | null>(null)
  const modelVersion = useModelVersion()
  useEffect(() => {
    if (!orgId) return
    let cancelled = false
    if (modelReadyFor && modelReadyFor !== orgId) clearResolvedModel()
    loadModel(orgId).then(() => { if (!cancelled) setModelReadyFor(orgId) })
    return () => { cancelled = true }
  }, [orgId])
  const modelLoading = Boolean(orgId) && modelReadyFor !== orgId
  const dViews = useMemo(buildViews, [modelVersion])

  //Initialize the Translation State
  const [isLoading, setIsLoading] = useState(true)


  const loadTranslations = async () => {
    await loadTrans()
    console.log('App.js Loaded Translations')
    setIsLoading(false)
    console.log(`Outlet-Key: ${location.pathname}/${snap.currentLang}`)
  }

  useEffect(
    () => {
      //Reads the Language from Local Storage, before loading the App
      const loadedLang = localStorage.getItem('language')
      if (loadedLang) {
        tState.currentLang = loadedLang
      }
      //Reads the Theme from Local Storage, before loading the App
      console.log('App.js loading the Theme')
      const loadedTheme = getFromLS('Theme')
      if (loadedTheme) {
        console.log('LOADED THEME:', loadedTheme)
        if (loadedTheme?.themeMode) ThemeContext.currentMode = loadedTheme.themeMode
        if (loadedTheme?.themeColor) ThemeContext.currentColor = loadedTheme.themeColor
        if (loadedTheme?.themeStyle) ThemeContext.currentStyle = loadedTheme.themeStyle
        if (loadedTheme?.themeNavMode) ThemeContext.currentNavMode = loadedTheme.themeNavMode
      }

      manageClientId()
    }, []
  )

  // Reload the translations when language changes.
  useEffect(() => {
    console.log(('App.js Load Translation on Change of Language'))
    loadTranslations()
  }, [snap.currentLang])

  // A new model can carry new text (the org's txt overlays): reload the
  // current slug's files, which consult the registry as they load.
  useEffect(() => {
    if (modelReadyFor) loadTrans()
  }, [modelVersion])

  if (isLoading) {
    return (<Loader height='50vh' size='2rem' />)
  } else {
    return (
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <GlobalStyle />
          {modelLoading && (
            <div data-testid='app.modelLoading' style={{
              position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'center',
              justifyContent: 'center', background: (theme as any).area?.bodyBg
            }}>
              <Loader height='50vh' size='2rem' />
            </div>
          )}
          <Router>
            <ModelSwap />
            <ModalStackProvider>

              <Routes>
                <Route path="/" element={<Welcome />} />
                <Route path="/loggedout" element={<LoggedOut />} />
                {dEntryRoutes}
                {/* Portal routes — no user auth required */}
                {dPortalRoutes}
                <Route element={<RequireAuth />} >
                  <Route element={<Layout />} >
                    {dViews}
                    <Route path="/home/settings" element={<AppBoard />} />
                    <Route path="/hello" element={<Hello />} />
                    <Route path="/org" element={<CreateOrg />} />
                    <Route path="/test" element={<SkeletonLoaderTest />} />
                    {/* Catch all */}
                    <Route path="/*" element={<Missing />} />
                  </Route>
                </Route>
              </Routes>
            </ModalStackProvider>
          </Router>
        </ThemeProvider>
        {/* <ReactQueryDevtools initialIsOpen={false} /> */}
      </QueryClientProvider>
    )
  }
}

export default App
